/**
 * SessionManager - Centralized session management with retry logic
 * 
 * This service provides:
 * - getSession(): Get current session (sessionToken + playerId from server)
 * - clearSession(): Reset cached session state
 * - refreshSession(): Force re-validate and refresh session
 * - withSessionRetry(): Wraps RPC calls with auto-retry on invalid_session
 * 
 * SECURITY: player_id is always fetched from server via validate_player_session,
 * never trusted from client storage.
 * 
 * Usage:
 *   const session = await SessionManager.getSession();
 *   // or use withSessionRetry for auto-recovery:
 *   const result = await SessionManager.withSessionRetry(async (s) => {
 *     return supabase.rpc('my_rpc', { p_session_token: s.sessionToken, ... });
 *   });
 */

import { supabase } from '@/integrations/supabase/client';
import { log } from '@/lib/logger';
import SessionTokenManager from './sessionTokenManager';

export interface SessionInfo {
  sessionToken: string;
  playerId: string;
  playerName?: string;
  groupId?: string;
  expiresAt?: string;
}

// Error patterns that indicate invalid/expired session
const INVALID_SESSION_PATTERNS = [
  'Invalid or expired session',
  'Invalid or expired session token',
  'invalid_session',
  'P0001', // PostgreSQL custom exception code
];

/**
 * Check if an error indicates an invalid/expired session
 */
export function isInvalidSessionError(error: unknown): boolean {
  if (!error) return false;
  
  const errorStr = typeof error === 'string' 
    ? error 
    : JSON.stringify(error);
  
  return INVALID_SESSION_PATTERNS.some(pattern => 
    errorStr.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * Check if RPC result indicates invalid session (for RPCs returning { ok: false, reason: 'invalid_session' })
 */
function isInvalidSessionResult(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  
  // Check for { ok: false, reason: 'invalid_session' }
  if (d.ok === false && typeof d.reason === 'string') {
    return d.reason.toLowerCase().includes('invalid_session');
  }
  
  // Check for { error: 'Invalid or expired session' }
  if (typeof d.error === 'string') {
    return INVALID_SESSION_PATTERNS.some(pattern => 
      (d.error as string).toLowerCase().includes(pattern.toLowerCase())
    );
  }
  
  return false;
}

class SessionManagerClass {
  private cachedSession: SessionInfo | null = null;
  private refreshPromise: Promise<SessionInfo | null> | null = null;
  private sessionExpiredCallback: (() => void) | null = null;

  /**
   * Register a callback to be called when session expires
   * Used by PlayerProtectedRoute to show re-auth UI
   */
  onSessionExpired(callback: () => void): void {
    this.sessionExpiredCallback = callback;
  }

  /**
   * Notify that session has expired
   */
  private notifySessionExpired(): void {
    if (this.sessionExpiredCallback) {
      this.sessionExpiredCallback();
    }
  }

  /**
   * Check if session appears valid (quick client-side check)
   */
  isSessionValid(): boolean {
    if (!this.cachedSession) {
      // Try to check stored token
      const token = SessionTokenManager.getPersistedToken();
      if (!token) return false;
      
      // Check expiry
      return !SessionTokenManager.isTokenExpired();
    }
    
    // Check cached session expiry
    if (this.cachedSession.expiresAt) {
      const expiryDate = new Date(this.cachedSession.expiresAt);
      return expiryDate.getTime() > Date.now();
    }
    
    return true;
  }

  /**
   * Get current session. Returns cached if valid, otherwise tries to restore from storage.
   * SECURITY: player_id comes from server validation, not client storage.
   */
  async getSession(): Promise<SessionInfo | null> {
    // Prefer cached in-memory session: it was set by usePlayerAuth right after
    // a successful login/restore and is the source of truth while the tab is open.
    // Only fall back to storage checks when we have nothing in memory.
    if (this.cachedSession) {
      if (this.cachedSession.expiresAt) {
        const expiryMs = new Date(this.cachedSession.expiresAt).getTime();
        if (Number.isFinite(expiryMs) && expiryMs - 30_000 > Date.now()) {
          return this.cachedSession;
        }
      } else {
        // No expiry recorded on cached session – trust it for this tick.
        return this.cachedSession;
      }
    }

    // No in-memory session. Check storage expiry as a fast bailout.
    if (SessionTokenManager.isTokenExpired()) {
      log.debug('[session] Token expired, needs re-auth');
      this.clearSession();
      this.notifySessionExpired();
      return null;
    }

    // Try to bootstrap from storage
    const bootstrapResult = await SessionTokenManager.bootstrap();

    if (!bootstrapResult) {
      log.debug('[session] No valid session');
      this.notifySessionExpired();
      return null;
    }


    // Fetch additional player info from server
    try {
      const { data, error } = await supabase.rpc('validate_player_session', {
        p_session_token: bootstrapResult.token
      });

      if (error || !data || !Array.isArray(data) || data.length === 0) {
        log.debug('[session] Token validation failed');
        this.clearSession();
        this.notifySessionExpired();
        return null;
      }

      const sessionData = data[0];

      this.cachedSession = {
        sessionToken: bootstrapResult.token,
        playerId: sessionData.player_id, // From server
        playerName: sessionData.display_name,
        groupId: sessionData.group_id,
        expiresAt: undefined // Will be set if available
      };

      log.debug('[session] Bootstrap ok');

      return this.cachedSession;
    } catch (err) {
      log.error('[session] Validation error', err);
      this.clearSession();
      this.notifySessionExpired();
      return null;
    }
  }

  /**
   * Update cached session (called by usePlayerAuth when session is created)
   */
  setSession(session: SessionInfo): void {
    this.cachedSession = session;
    log.debug('[session] Session set');
  }

  /**
   * Clear cached session state
   */
  clearSession(): void {
    this.cachedSession = null;
    this.refreshPromise = null;
    SessionTokenManager.softReset();
    log.debug('[session] Session cleared');
  }

  /**
   * Force refresh session from stored credentials
   */
  async refreshSession(): Promise<SessionInfo | null> {
    // Avoid concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._doRefreshSession();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  private async _doRefreshSession(): Promise<SessionInfo | null> {
    log.info('[session] Refreshing');
    
    // Clear current cache
    this.cachedSession = null;

    // Try to get current user and create new session
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        log.debug('[session] No auth user for refresh');
        this.clearSession();
        this.notifySessionExpired();
        return null;
      }

      // Find default player
      const { data: players, error: playerError } = await supabase
        .from('players')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_default', true)
        .eq('is_active', true);

      if (playerError || !players || players.length === 0) {
        log.debug('[session] No default player found');
        return null;
      }

      const player = players[0];

      // Create new session via SessionTokenManager
      const sessionResult = await SessionTokenManager.createSession(player.id);

      if (!sessionResult) {
        log.error('[session] Failed to create session');
        return null;
      }

      // Store new session
      this.cachedSession = {
        sessionToken: sessionResult.token,
        playerId: player.id,
        playerName: player.display_name,
        groupId: player.player_group_id,
        expiresAt: undefined
      };

      log.info('[session] Refreshed successfully');
      return this.cachedSession;
    } catch (err) {
      log.error('[session] Refresh error', err);
      return null;
    }
  }

  /**
   * Wrap an RPC call with automatic retry on invalid_session.
   * 
   * Usage:
   *   const { data, error } = await SessionManager.withSessionRetry(async (session) => {
   *     return supabase.rpc('my_rpc', { p_session_token: session.sessionToken, ... });
   *   });
   */
  async withSessionRetry<T>(
    fn: (session: SessionInfo) => Promise<T>,
    options?: { maxRetries?: number }
  ): Promise<T> {
    const maxRetries = options?.maxRetries ?? 1;
    let lastError: unknown = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const session = await this.getSession();
      
      if (!session) {
        // Try to refresh session if none exists
        const refreshed = await this.refreshSession();
        if (!refreshed) {
          this.notifySessionExpired();
          throw new Error('No valid session available');
        }
      }

      const currentSession = this.cachedSession;
      if (!currentSession) {
        this.notifySessionExpired();
        throw new Error('No valid session available');
      }

      try {
        const result = await fn(currentSession);
        
        // Check if result indicates invalid session (for RPCs that return { ok: false, reason: 'invalid_session' })
        if (isInvalidSessionResult(result)) {
          if (attempt < maxRetries) {
            log.warn('[session] Invalid session in result, retrying');
            this.clearSession();
            continue;
          }
          log.error('[session] Retry failed - still invalid');
          this.notifySessionExpired();
          throw new Error('Session invalid after retry');
        }

        // Check for Supabase-style error in result
        const supabaseResult = result as { error?: { message?: string; code?: string } };
        if (supabaseResult.error && isInvalidSessionError(supabaseResult.error)) {
          if (attempt < maxRetries) {
            log.warn('[session] Invalid session in error, retrying');
            this.clearSession();
            continue;
          }
          log.error('[session] Retry failed - still invalid');
          this.notifySessionExpired();
          throw supabaseResult.error;
        }

        return result;
      } catch (err) {
        lastError = err;
        
        if (isInvalidSessionError(err)) {
          if (attempt < maxRetries) {
            log.warn('[session] Invalid session exception, retrying');
            this.clearSession();
            continue;
          }
          log.error('[session] Session retry failed', err);
          this.notifySessionExpired();
        }
        throw err;
      }
    }

    throw lastError || new Error('Session retry exhausted');
  }

  /**
   * Get current cached session synchronously (may be null)
   */
  getCachedSession(): SessionInfo | null {
    return this.cachedSession;
  }
}

// Export singleton instance
export const SessionManager = new SessionManagerClass();
export default SessionManager;
